import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/utils/cn";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  buildMinDateDisabled,
  isDateInAnyRanges,
  formatDateString,
  parseDateString,
  type DateRangeYmd,
} from "./datePickerRules";

interface SharedEndDatePickerProps {
  label: string;
  description?: string;
  required?: boolean;
  joiningDate: string;
  value: string;
  onChange: (value: string) => void;
  isError?: boolean;
  disabled?: boolean;
  clearButtonLabel?: string;
  onClear?: () => void;
  occupiedRanges?: DateRangeYmd[];
}

export default function SharedEndDatePicker({
  label,
  description,
  required,
  joiningDate,
  value,
  onChange,
  isError,
  disabled,
  clearButtonLabel,
  onClear,
  occupiedRanges = [],
}: SharedEndDatePickerProps) {
  const [open, setOpen] = useState(false);

  const selectedDate = useMemo(() => parseDateString(value), [value]);
  const minDate = useMemo(() => parseDateString(joiningDate), [joiningDate]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-[14px] font-semibold text-[#939393]">
            {label}
          </label>
          {required && <span className="text-[12px] text-[#C0392B]">*</span>}
          {description && (
            <span className="text-[12px] text-[#939393]">{description}</span>
          )}
        </div>
        {clearButtonLabel && onClear && (
          <Button
            type="button"
            variant="ghost"
            className="h-7 px-2 text-[12px] text-[#0E59C6] hover:text-[#0E59C6]"
            onClick={onClear}
            disabled={disabled ?? false}
          >
            {clearButtonLabel}
          </Button>
        )}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={!joiningDate || (disabled ?? false)}
            className={cn(
              "w-full h-[35px] justify-between px-[15px] py-2 text-[15px] font-normal hover:bg-white",
              isError ? "border-destructive" : "border-[#D6D6DE]"
            )}
          >
            {value || "YYYY-MM-DD"}
            <ChevronDown className="h-4 w-4 text-[#939393]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              onChange(date ? formatDateString(date) : "");
              setOpen(false);
            }}
            disabled={(date) =>
              buildMinDateDisabled(minDate)(date) ||
              isDateInAnyRanges(date, occupiedRanges)
            }
            captionLayout="label"
          />
        </PopoverContent>
      </Popover>
      {isError && <p className="text-destructive text-xs">* 필수 값입니다</p>}
    </div>
  );
}
